import { X } from "lucide-react";
import { CostEstimateResponse } from "./sms/types";

// ─── Cost Breakdown Modal ─────────────────────────────────────────────────────
interface CostBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  costEstimate: CostEstimateResponse | null;
  channel?: string;
}

export const CostBreakdownModal: React.FC<CostBreakdownModalProps> = ({
  isOpen,
  onClose,
  costEstimate,
}) => {
  if (!isOpen || !costEstimate) return null;

  const { country_breakdown = [], estimated_cost = 0, currency_symbol = '₹' } = costEstimate;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Cost breakdown by country</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Costs are estimated based on WhatsApp pricing for the selected countries
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Country</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Country Code</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500">Contacts</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500">Rate</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500">Estimated Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {country_breakdown.map((country, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-900">
                    {country.country_name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-gray-600 font-mono">{country.country_code}</td>
                  <td className="px-6 py-4 text-right font-medium">
                    {country.contacts?.toLocaleString() || 0}
                  </td>
                  <td className="px-6 py-4 text-right text-gray-600">
                    {country?.currency_symbol} {country.rate_per_unit?.toFixed(4) || '0.00'}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold">
                    {country?.currency_symbol} {country.estimated_cost?.toFixed(2) || '0.00'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer Total */}
        <div className="bg-gray-50 border-t px-6 py-4 flex justify-between items-center">
          <div className="font-semibold text-gray-900">Total Cost</div>
          <div className="font-bold text-xl text-gray-900">
            {currency_symbol} {estimated_cost.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
};